import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import api, { extractApiError } from "@/lib/api";

/**
 * VerifyFundsDialog — admin action: confirm funds for an order,
 * record driver + farmer payouts, push ledger entries and notifications.
 */
export default function VerifyFundsDialog({ order, onSuccess }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    driver_payout: "",
    farmer_payout: "",
    payout_method: "paypal",
    note: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      const body = {
        driver_payout: parseFloat(form.driver_payout || "0") || 0,
        farmer_payout: parseFloat(form.farmer_payout || "0") || 0,
        payout_method: form.payout_method,
        note: form.note?.trim() || null,
      };
      const { data } = await api.post(`/admin/orders/${order.order_id}/verify-funds`, body);
      toast.success(`Funds verified. ${data.ledger_entries} ledger entries created.`);
      setOpen(false);
      onSuccess?.();
    } catch (err) {
      toast.error(extractApiError(err, "Could not verify funds."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          data-testid={`verify-${order.order_id}`}
          disabled={order.funds_verified}
          className={
            order.funds_verified
              ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100 cursor-default"
              : "bg-emerald-800 hover:bg-emerald-700 text-stone-50"
          }
        >
          <CheckCircle2 size={14} className="mr-1.5" />
          {order.funds_verified ? "Verified" : "Verify funds"}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-stone-50 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-stone-900">
            Verify &amp; confirm order
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm font-sans">
          <div className="bg-white border border-stone-200 rounded-md p-3 text-stone-700">
            <div className="font-serif text-base text-stone-900">{order.product_title}</div>
            <div className="text-xs text-stone-500 mt-1">
              {order.client_name} → {order.farmer_name}
              {order.driver_name ? ` · driver: ${order.driver_name}` : " · no driver"}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="driver_payout" className="text-xs font-semibold text-stone-800">
                Driver payout ($)
              </Label>
              <Input
                id="driver_payout"
                data-testid="vf-driver-payout"
                type="number"
                min="0"
                step="0.01"
                value={form.driver_payout}
                onChange={(e) => setForm({ ...form, driver_payout: e.target.value })}
                disabled={!order.driver_id}
                placeholder={order.driver_id ? "0.00" : "no driver"}
                className="mt-2 bg-white"
              />
            </div>
            <div>
              <Label htmlFor="farmer_payout" className="text-xs font-semibold text-stone-800">
                Hobby farmer payout ($)
              </Label>
              <Input
                id="farmer_payout"
                data-testid="vf-farmer-payout"
                type="number"
                min="0"
                step="0.01"
                value={form.farmer_payout}
                onChange={(e) => setForm({ ...form, farmer_payout: e.target.value })}
                placeholder="0.00"
                className="mt-2 bg-white"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold text-stone-800">Payout method</Label>
            <Select
              value={form.payout_method}
              onValueChange={(v) => setForm({ ...form, payout_method: v })}
            >
              <SelectTrigger data-testid="vf-method" className="mt-2 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="paypal">PayPal</SelectItem>
                <SelectItem value="cashapp">CashApp</SelectItem>
                <SelectItem value="bank">Bank Transfer</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="note" className="text-xs font-semibold text-stone-800">
              Note (optional)
            </Label>
            <Textarea
              id="note"
              data-testid="vf-note"
              rows={2}
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="e.g., bank transfer ref #1234"
              className="mt-2 bg-white"
            />
          </div>
          <p className="text-xs text-stone-500">
            On confirm, ledger entries are created and the client, driver, and hobby farmer receive a
            confirmation notification.
          </p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} data-testid="vf-cancel">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            data-testid="vf-submit"
            className="bg-emerald-800 hover:bg-emerald-700 text-stone-50"
          >
            {submitting ? "Verifying…" : "Verify & confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
